import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import * as Yup from "yup";
import { daysOfWeek } from "../../data";
import AreYouSureModal from "../../modals/AreYouSureModal";
import { Input } from "../Input";
import LeftSide from "../LeftSide";
import MultiSelectDropdown from "../MultiSelect";
import Popover from "../Popovers";
import StartAndEndTime from "../StartAndEndTime";
import classes from "./SlotForm.module.css";
import useApi from "../../useApi";
import { toast } from "react-toastify";

const initialValues = {
  courtStartTime: null,
  courtEndTime: null,
  courtCharges: "",
  courtDayOfWeek: [],
  active: true,
};

const validationSchema = Yup.object().shape({
  courtStartTime: Yup.string().required("Start time is required"),
  courtEndTime: Yup.mixed()
    .required("End time is required")
    .test(
      "is-after-start",
      "End time must be after start time",
      function (endTime) {
        const { courtStartTime } = this.parent;

        // If either time is missing, skip validation
        if (!courtStartTime || !endTime) return true;

        // Convert to comparable values
        let startTimeValue, endTimeValue;

        // Handle Date objects
        if (courtStartTime instanceof Date && endTime instanceof Date) {
          // Extract hours and minutes for comparison
          startTimeValue =
            courtStartTime.getHours() * 60 + courtStartTime.getMinutes();
          endTimeValue = endTime.getHours() * 60 + endTime.getMinutes();
        }
        // Handle string values like "HH:MM" or "HH:MM:SS"
        else if (
          typeof courtStartTime === "string" &&
          typeof endTime === "string"
        ) {
          // Parse time strings
          const startParts = courtStartTime.split(":").map(Number);
          const endParts = endTime.split(":").map(Number);

          // Convert to minutes for easy comparison
          startTimeValue = (startParts[0] || 0) * 60 + (startParts[1] || 0);
          endTimeValue = (endParts[0] || 0) * 60 + (endParts[1] || 0);
        }
        // For any other format, log and allow (you might need to add specific handling)
        else {
          console.log("Unknown time format:", courtStartTime, endTime);
          return true;
        }

        return endTimeValue > startTimeValue;
      }
    ),
  courtCharges: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(0, "Amount cannot be negative"),
  courtDayOfWeek: Yup.array()
    .of(Yup.number().required())
    .min(1, "At least one day of week must be selected"),
});

const SlotForm = ({
  onSubmit,
  initialData,
  onCancel,
  index,
  onDelete,
  onChange,
  slot,
  value,
  editMode,
  setCourtsTiming,
  slotsArray,
 
}) => {
  const { deleteCourtTiming, addCourtTiming } = useApi();
  const [slotIndx, setSlotIndx] = useState(-1);
  const [edit, setEdit] = useState(false);
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [deleteIndx, setDeleteIndx] = useState(null);
  const [showDayDeleteModal, setShowDayDeleteModal] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState(null);

  console.log("EditMode", editMode);
  const formik = useFormik({
    initialValues: initialData || initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      console.log("Form submitted with values:", values);
      setEdit(true);

      const updatedSlots = [...value];
      updatedSlots[index] = {
        ...values,
        courtDayOfWeek: values?.courtDayOfWeek?.map((item) => ({
          days: item,
          courtTimingId: slot?.courtTimingId,
        })),
      };
      // if edit mode is true and slot is not present then add the slot
      if (editMode && !slot?.courtTimingId) {
        const addTimeParams = {
          courtDayOfWeek: values?.courtDayOfWeek,
          courtId: +editMode,
          courtTimingId: null,
          courtStartTime: moment(values?.courtStartTime)
            .set({ second: 0 })
            .format("HH:mm:ss"),
          courtEndTime: moment(values?.courtEndTime)
            .set({ second: 0 })
            .format("HH:mm:ss"),
          courtCharges: values?.courtCharges,
          active: true,
        };
        addCourtTimingApi(addTimeParams);
      }

      onChange(updatedSlots);
      setEdit(true);

      setSubmitting(false);
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    resetForm,
    setFieldError,
  } = formik;

  // Map day IDs to day names from daysOfWeek array
  const getDayNames = () => {
    if (!slot?.courtDayOfWeek || !Array.isArray(slot?.courtDayOfWeek)) {
      return [];
    }

    return slot?.courtDayOfWeek.map((timeData) => {
      const dayInfo = daysOfWeek.find((d) => d.value === timeData?.days);
      return {
        id: timeData?.courtTimingId,
        label: dayInfo ? dayInfo.label : `Day ${timeData?.courtTimingId}`,
      };
    });
  };

  // Generate menu items for the Popover
  const generateDayMenuItems = () => {
    const dayNames = getDayNames();
    console.log("Day names in popover:", dayNames);

    return dayNames.map((day) => ({
      label: day.label,
      command: () => {
        console.log(`Selected day to delete: ${day.label} (ID: ${day.id})`);
        setSelectedDayId(day.id);
        setShowDayDeleteModal(true);
      },
    }));
  };

  // Handle deleting a specific day
  const handleDayDelete = async () => {
    if (selectedDayId === null) return;
    const response = await deleteCourtTiming({ courtTimingId: selectedDayId });
    console.log("response", response);
    if (response?.data?.headers?.error === 200) {
      // Filter out the selected day
      toast.success("Day deleted successfully");

      if (values?.courtDayOfWeek?.length === 1) {
        const updatedSlots = [...value];
        onChange(
          updatedSlots?.filter((item) => item?.courtTimingId !== selectedDayId)
        );
      }
      console.log("values.courtDayOfWeek", values.courtDayOfWeek);
      const updatedDays = slot?.courtDayOfWeek.filter(
        (dayId) => dayId?.days !== selectedDayId
      );

      // Update the form and parent component
      const updatedValues = {
        ...values,
        courtDayOfWeek: updatedDays,
      };

      setFieldValue("courtDayOfWeek", updatedDays);

      const updatedSlots = [...value];
      updatedSlots[index] = updatedValues;
      onChange(updatedSlots);

      setShowDayDeleteModal(false);
      setSelectedDayId(null);
    } else {
      toast.error(response?.data?.headers?.message);
    }
  };

  // Check if the slot has day of week data
  const hasDayOfWeekData = () => {
    return values?.courtDayOfWeek && values.courtDayOfWeek.length > 0;
  };

  const handleChangeWithTouch = (field) => (value) => {
    setFieldValue(field, value);
    setFieldTouched(field, true, false);
  };

  const handleTimeChange = (timeType, value) => {
    const field = timeType === "start" ? "courtStartTime" : "courtEndTime";
    handleChangeWithTouch(field)(value);
  };

  // Handle form submission with explicit event capturing
  const handleFormSubmitWithoutRefresh = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Stop event bubbling
    }

    // Call Formik's submit handler
    handleSubmit(e);

    // Return false to ensure no default behavior
    return false;
  };

  const deleteCourtTimingApi = async (params) => {
    const res = deleteCourtTiming(params);
    console.log("res", res);
  };
  // Handle button click instead of form submit
  const handleButtonClick = (index, e) => {
    setSlotIndx(index);
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(index);
  };

  const handleChange = (index, field, value) => {
    const updatedSlots = [...values.courtTimings];
    updatedSlots[index][field] = value;
    setFieldValue("courtTimings", updatedSlots);
  };

  const addCourtTimingApi = async (params) => {
    const res = await addCourtTiming(params);
    if (res?.data?.headers?.error === 200) {
      onchange([...slotsArray, res?.data?.body[0]]);
      toast.success("Slot Created Successfully");
      toast.success("Slot added successfully");
    } else {
      toast.error(res?.data?.headers?.message);
    }
  };

  useEffect(() => {
    if (slot) {
      console.log("inside useEffect slot", slot);
      setEdit(true);
      console.log("inside slots", slot);
      setFieldValue(
        "courtDayOfWeek",
        slot?.courtDayOfWeek?.map((item) => item?.days)
      );
      setFieldValue("courtStartTime", moment(slot?.courtStartTime, "HH:mm:ss"));

      // For courtEndTime
      setFieldValue("courtEndTime", moment(slot?.courtEndTime, "HH:mm:ss"));
      setFieldValue("courtCharges", slot?.courtCharges);
    }
  }, [slot]);

  console.log("SlotForm", slot);
  return (
    // Use a div wrapper instead of form to avoid nesting forms
    <>
      <div className={classes.form}>
        <Row className={classes.slotRow}>
          <Col xs={12} md={3}>
            <MultiSelectDropdown
              disabled={edit}
              display="comma"
              options={daysOfWeek}
              placeholder="Select Days"
              selectionMode="multiple"
              value={values.courtDayOfWeek || []}
              onChange={(selectedValues) => {
                // Already returns the array of values, no need to map them
                setFieldValue("courtDayOfWeek", selectedValues);
                setFieldTouched("courtDayOfWeek", true, false);

                // Clear error if days are selected
                if (selectedValues && selectedValues.length > 0) {
                  setFieldError("courtDayOfWeek", undefined);
                }
              }}
              errorText={touched.courtDayOfWeek && errors.courtDayOfWeek}
            />
          </Col>
          <Col xs={12} md={5}>
            <div className={classes.timePickerContainer}>
              <StartAndEndTime
                disabled={edit}
                startTime={values.courtStartTime}
                endTime={values.courtEndTime}
                onChange={handleTimeChange}
                errors={{
                  startTime: touched.courtStartTime && errors.courtStartTime,
                  endTime: touched.courtEndTime && errors.courtEndTime,
                }}
              />
            </div>
          </Col>
          <Col xs={12} md={2}>
            <Input
              disabled={edit}
              type="number"
              placeholder="Enter charges"
              value={values.courtCharges}
              onChange={handleChangeWithTouch("courtCharges")}
              onBlur={handleBlur}
              leftIcon={<LeftSide right="Rs." />}
              customStyle={{ gap: "15px" }}
              inputStyle={{ paddingLeft: "0" }}
              errorText={touched.courtCharges && errors.courtCharges}
            />
          </Col>
          <Col xs={12} md={2} className={classes.actionColumn}>
            {edit && !editMode && (
              <div className={classes.actionButtons}>
                <button
                  className={clsx(classes.iconButton, classes.editButton)}
                  type="button"
                  onClick={() => {
                    // if (slot) {
                    //   return;
                    // }
                    setEdit(false);
                  }}
                >
                  <FiEdit />
                </button>

                {
                  <button
                    className={clsx(classes.iconButton, classes.deleteButton)}
                    type="button"
                    onClick={() => {
                      // if(slots){
                      //   return
                      // }
                      setShowAreYouSureModal(true);
                      setDeleteIndx(index);
                      console.log("ABCD");
                    }}
                  >
                    <FiTrash2 />
                  </button>
                }
              </div>
            )}{" "}
            {editMode && !slot?.courtTimingId && (
              <>
                <div className={classes.actionButtons}>
                  <button
                    type="button" // Changed from "submit" to "button"
                    className={classes.button}
                    onClick={(e) => handleButtonClick(index, e)}
                  >
                    Save
                  </button>
                  {/* <button
                    className={clsx(classes.iconButton, classes.deleteButton)}
                    type="button"
                    onClick={() => {
                      setShowAreYouSureModal(true);
                      setDeleteIndx(index);
                    }}
                  >
                    <FiTrash2 />
                  </button> */}
                </div>
              </>
            )}
            {editMode && hasDayOfWeekData() && (
              <div className={classes.actionButtons}>
                <Popover
                  items={generateDayMenuItems()}
                  className={classes.daysPopover}
                >
                  <button
                    className={clsx(classes.iconButton, classes.deleteButton)}
                    type="button"
                  >
                    <FiTrash2 />
                  </button>
                </Popover>
              </div>
            )}
            {editMode && !hasDayOfWeekData() && (
              <div className={classes.actionButtons}>
                <button
                  className={clsx(classes.iconButton, classes.deleteButton)}
                  type="button"
                  onClick={() => {
                    setShowAreYouSureModal(true);
                    setDeleteIndx(index);
                  }}
                >
                  <FiTrash2 />
                </button>
              </div>
            )}
          </Col>
        </Row>
      </div>

      {showAreYouSureModal && (
        <AreYouSureModal
          title="Slot"
          show={showAreYouSureModal}
          setShow={setShowAreYouSureModal}
          onConfirm={() => {
            onDelete(deleteIndx);
          }}
        />
      )}

      {showDayDeleteModal && (
        <AreYouSureModal
          title={`Delete Day: ${
            daysOfWeek.find((d) => d.value === selectedDayId)?.label ||
            "Selected Day"
          }`}
          show={showDayDeleteModal}
          setShow={setShowDayDeleteModal}
          onConfirm={handleDayDelete}
        />
      )}
    </>
  );
};

export default SlotForm;
